import type { UseStore } from 'idb-keyval';
import { clear, createStore, entries, set } from 'idb-keyval';
import type { GetRouteQuery } from '../../graphql';
import type { Distances, LocationPair } from './context';
import { parseRoute, routeParamsToString } from './distance-utils';
import type { ScenarioLocation } from '../../lib/planningData';
import type { MutableRefObject } from 'react';
import { DistanceMap } from './distanceMap';

let store: UseStore;

export const generateIdbKey = (
  pair: LocationPair,
  draft: number | null,
  locations: Map<number, ScenarioLocation>,
  noGoZones: string,
) => {
  const from = locations.get(pair[0]);
  const to = locations.get(pair[1]);

  if (!from || !to) {
    return;
  }

  const fromLong = from.longitude;
  const fromLat = from.latitude;
  const toLong = to.longitude;
  const toLat = to.latitude;

  if (!fromLong || !fromLat || !toLong || !toLat) {
    return;
  }

  const fLong = +fromLong;
  const fLat = +fromLat;
  const tLong = +toLong;
  const tLat = +toLat;

  return routeParamsToString([
    fLong,
    fLat,
    tLong,
    tLat,
    pair[2],
    pair[3] ?? draft,
    pair[4] ?? noGoZones ?? null,
  ]);
};

export function getDistanceIdb(): UseStore {
  // Update version if we need to clear the browser cache for users
  if (!store) store = createStore('distance-cache-9', 'keyval');
  return store;
}

export async function clearDistanceCache(): Promise<void> {
  return await clear(getDistanceIdb());
}

export const getStoredDistances = async (
  draft: number | null,
  locationsById: Map<number, ScenarioLocation>,
  distances: MutableRefObject<Distances>,
  noGoZones: string,
): Promise<Distances> => {
  const distanceEntries = await entries(getDistanceIdb());
  const storedDistances = new DistanceMap(
    distances.current.map,
    locationsById,
    draft,
    noGoZones,
  );
  const map = storedDistances.map;

  for (const [key, value] of distanceEntries) {
    if (!value || !key) continue;

    const [
      fromLongStr,
      fromLatStr,
      toLongStr,
      toLatStr,
      avoidChannelsStr,
      draftStr,
      noGoZones,
    ] = key.toString().split('_');
    const cachedDraft = draftStr !== 'null' ? +draftStr : null;

    try {
      const distance = parseRoute(value);
      map.set(
        distance,
        +fromLongStr,
        +fromLatStr,
        +toLongStr,
        +toLatStr,
        JSON.parse(avoidChannelsStr),
        cachedDraft,
        noGoZones,
      );
    } catch (err) {
      console.error('route parsing failed', err);
    }
  }

  return storedDistances;
};

export const setStoredDistance = (
  locationPair: LocationPair,
  draft: number | null,
  data: GetRouteQuery,
  locations: Map<number, ScenarioLocation>,
  noGoZones: string,
) => {
  const idbKey = generateIdbKey(locationPair, draft, locations, noGoZones);
  if (idbKey) {
    void set(idbKey, data, getDistanceIdb());
  }
};
